import moment from 'moment'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { createSelector } from 'reselect'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import {
  findPostPurchaseCheckout,
  getConnectRoomsSpecialRequestMessage,
  isBedbankItem,
  isBNBLLEHotelItem,
  isCarHireItem, isFlightItem,
  isInstantBookingLEHotelItem,
  isTourV1Item
} from 'lib/checkout/checkoutUtils'
import { groupBy, sum } from 'lib/array/arrayUtils'
import {
  CHECKOUT_ITEM_TYPE_FLIGHT,
  GIFT_RECIPIENT_FORM_ID,
  PRIMARY_TRAVELLER_FORM_ID,
} from 'constants/checkout'
import { ITEM_TYPE_BEDBANK, ITEM_TYPE_EXPERIENCE, ITEM_TYPE_GIFT_CARD, ITEM_TYPE_INSURANCE, ITEM_TYPE_BOOKING_PROTECTION } from 'constants/cart'
import config from 'constants/config'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { computeBaggageCostForPaxList } from 'checkout/lib/utils/flights/extras'
import { OFFER_DYNAMIC_PRICING_TYPES, OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { buildBaggagePayload, buildPassengerPayload } from 'checkout/lib/utils/flights/order'
import { buildTravellerInfo } from 'checkout/lib/utils/experiences/order'
import { getJourneyV2IdKey } from 'lib/flights/flightUtils'
import { excludeNullOrUndefined } from 'checkout/utils'
import { Order } from '@luxuryescapes/contract-svc-order'
import { generateScheduleDate } from 'lib/payment/gift'
import { getInstantBookingPriceAndSurcharge } from 'checkout/lib/utils/accommodation/price'
import getTransferItems from 'checkout/selectors/view/getTransferItems'
import getInsuranceItemsView from 'checkout/selectors/view/getInsuranceItemsView'
import { getAge } from 'lib/datetime/dateUtils'
import { TRANSPORTATION_CODE } from 'components/CarHire/CarHireHardcodedLists'
import {
  checkoutAccommodationOfferView,
  getLeInstantBookingItems
} from 'checkout/selectors/view/accommodation'
import { getBundleAndSaveItemViews } from 'checkout/selectors/view/bundleAndSave'
import { getGiftCardItems } from 'checkout/selectors/view/giftCard'
import { getTravellerFormSchemaRequest, isTravellerFormNeeded } from 'checkout/selectors/request/travellerSchema'
import { baggageOptionsMap } from 'api/lib/convertJourneyV2'
import { getVillaViews } from '../view/villa'
import { getBookingProtectionItems } from '../view/bookingProtection'
import { getFullOffers } from 'selectors/offerSelectors'
import getBookingProtectionDetails from 'checkout/selectors/request/getBookingProtectionDetails'
import { getFlightBreakdownView } from 'checkout/selectors/view/flights'
import { getBedbankChangeDatesTravellers } from '../bedbankChangeDatesTravellers'
import { getCoverGeniusAgeCategoryByDateOfBirth } from 'lib/insurance/insuranceHelpers'
import { getCarHireSelectedRateOption } from 'checkout/lib/utils/carHire/view'
import { FlightViewTypes } from 'constants/flight'
import { floatify } from 'lib/maths/mathUtils'
import { getChannelMarkup } from 'selectors/channelMarkupSelector'
import { filterItemDiscounts, PromoItemDiscountsWithStatus } from 'lib/promo/promoMappers'
import { getAccommodationAndExperienceItemDiscounts, getAllPromoCodeItemDiscounts, getInsuranceItemDiscounts, getTransferItemDiscounts } from './checkout'
import getCheckoutTravellerSchema from 'checkout/selectors/getCheckoutTravellerSchema'
import getExperienceItemSourceApp from './getExperienceItemSourceApp'
import formatItemDiscount from 'lib/payment/formatDiscountItem'
import { isLebtServiceFeesEnabled } from 'businessTraveller/selectors/businessTravellerServiceFeesSelectors'

function getContactName(primaryContact: App.Checkout.TravellerForm | undefined, account: App.AuthAccount) {
  if (primaryContact) {
    return `${primaryContact.data.firstName} ${primaryContact.data.lastName}`
  } else {
    let contactName = account.givenName
    if (account.surname) contactName += ` ${account.surname}`
    return contactName
  }
}

export const getFormattedTravellers = createSelector(
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => getCheckoutTravellerSchema(state),
  (travellerForms, schemaData) => {
    if (schemaData && travellerForms.length > 0) {
      return buildTravellerInfo(schemaData.accommodationFlightSchema, travellerForms)
    }
  },
)

const getFormattedTransferItems = createSelector(
  (state: App.State) => getTransferItems(state),
  (state: App.State) => state.experience.experiences,
  (state: App.State) => getTransferItemDiscounts(state),
  (state: App.State) => getExperienceItemSourceApp(state),
  (transferItems, experiences, itemDiscounts, sourceApp) => {
    return transferItems.map(item => {
      const experience = experiences[item.experienceId]
      const { transfer, transactionKey } = item
      if (experience && transfer.option && itemDiscounts.hasRequiredPromoCodeData) {
        const [hour, minute] = transfer.time?.split(':') ?? ['00', '00']
        const fullDateTime = moment.utc(transfer.date).minute(parseInt(minute)).hour(parseInt(hour)).format('YYYY-MM-DD[T]HH:mm:ss')

        const price = transfer.option.price

        return {
          type: ITEM_TYPE_EXPERIENCE,
          provider_offer_id: experience.id,
          brand: config.BRAND,
          transaction_key: transactionKey,
          total: price,
          taxes_and_fees: transfer.option.taxesAndFees,
          tickted: experience.ticketed,
          title: experience.name,
          categories: experience.categories,
          cancellation_policies: experience.cancellationPolicies.length > 0 ? {
            timezoneOffset: `${new Date().getTimezoneOffset()}`,
            refundPolicies: experience.cancellationPolicies.map(policy => ({
              id: policy.id,
              periodLabel: policy.periodLabel,
              periods: policy.periods,
              type: policy.type,
              value: policy.value,
            })),
          } : undefined,
          le_exclusive: experience.leExclusive,
          special_requests: transfer.specialRequests,
          pickup_point_name: transfer.type === 'HOTEL-TO-AIRPORT' ? transfer.hotel.name : experience.airport?.name,
          pickup_point_id: transfer.type === 'HOTEL-TO-AIRPORT' ? transfer.hotel.googlePlaceId : experience.airport?.code,
          dropoff_point_name: transfer.type === 'HOTEL-TO-AIRPORT' ? experience.airport?.name : transfer.hotel.name,
          dropoff_point_id: transfer.type === 'HOTEL-TO-AIRPORT' ? experience.airport?.code : transfer.hotel.googlePlaceId,
          deal_options: {
            adults: transfer.travellers.adults,
            children: transfer.travellers.children ?? 0,
            child_seats: transfer.travellers.childSeats ?? 0,
            booster_seats: transfer.travellers.boosterSeats ?? 0,
            option: transfer.option,
            flight_number: transfer.flightNumber,
            type: transfer.type,
            bags: transfer.baggage?.bags ?? 0,
            oversized_bags: transfer.baggage?.oversizedBags ?? 0,
          },
          ticket: {
            date: fullDateTime,
            // time is optional so we need separate fields to know whether it was set or not
            day: transfer.date,
            time: transfer.time,
            fareType: transfer.option.name,
            identifier: transfer.option.id,
            productId: transfer.option.id,
            type: transfer.type,
          },
          book_by_date: transfer.option?.bookByDate,
          ...((transfer.option?.discounts?.app.amount ?? 0) > 0 && {
            app_discount_amount: transfer.option.discounts?.app.amount,
            app_discount_percent: transfer.option.discounts?.app.percentage,
          }),
          member_discount_amount: 0,
          item_discounts: itemDiscounts.itemDiscounts.map(formatItemDiscount),
          source_app: sourceApp,
        }
      }

      return null
    })
  },
)

const getFormattedBundleAndSaveItems = createSelector(
  (state: App.State) => getBundleAndSaveItemViews(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.auth.account,
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (views, travellerForms, account, specialRequests, promoCodeItemDiscounts) => {
    const data = views.data

    const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

    return data.flatMap((view) => {
      return view.hotels.map(hotelView => {
        const hotelViewPrice = hotelView.totals.price
        const newPrice = view.item.dates[hotelView.offer.id]?.newPrice
        const newSurcharge = view.item.dates[hotelView.offer.id]?.newSurcharge
        const newExtraGuestSurcharge = view.item.dates[hotelView.offer.id]?.newExtraGuestSurcharge

        const itemDiscounts = filterItemDiscounts({ itemDiscounts: promoCodeItemDiscounts.itemDiscounts, lookup: { specificType: 'cart-id-based', cartItemIds: [view.item.itemId] } })
        // TODO: We don't have an official type for order item creation, create it
        const orderItem: any = {
          bundle_offer_id: view.bundleOffer.id,
          bundle_package_id: view.bundlePackage.id,
          reservation_type: RESERVATION_TYPE_INSTANT_BOOKING,
          check_in: moment.utc(hotelView.checkIn).format(ISO_DATE_FORMAT),
          check_out: moment.utc(hotelView.checkOut).format(ISO_DATE_FORMAT),
          number_of_nights: hotelView.duration,
          number_of_adults: view.occupancy.adults,
          number_of_children: view.occupancy.children,
          number_of_infants: view.occupancy.infants,
          children_ages: view.occupancy.childrenAge ?? [],
          rate_plan_id: hotelView.package.roomRate?.id,
          guest_first_name: primaryTraveller?.data.firstName,
          guest_last_name: primaryTraveller?.data.lastName,
          cart_item_id: view.item.itemId,
          offer_id: hotelView.offer.id,
          package_id: hotelView.package.id,
          room_rate_id: hotelView.package.roomRate?.id,
          transaction_key: uuidV4(),
          offer_booking_type: 'reservation',
          offer_type: hotelView.offer.type,
          offer_name: hotelView.offer.name,
          package_name: hotelView.package.name,
          offer_slug: view.bundleOffer.slug,
          price: newPrice || hotelViewPrice,
          value: hotelView.package.value,
          surcharge: newSurcharge ?? hotelView.totals.surcharge - hotelView.totals.extraGuestSurcharge,
          guest_special_requests: specialRequests[view.item.itemId],
          extra_guest_surcharge: newExtraGuestSurcharge ?? hotelView.totals.extraGuestSurcharge,
          property_fees: hotelView.totals.otherFees?.propertyFees,
          localizedPrices: hotelView.package.trackingPrice,
          customer_email: primaryTraveller?.data.email,
          customer_name: account?.fullName,
          room_rate_amount: undefined,
          item_discounts: itemDiscounts.map(formatItemDiscount),
        }

        if (OFFER_DYNAMIC_PRICING_TYPES.includes(hotelView.offer.type)) {
          orderItem.room_rate_amount = newPrice || hotelViewPrice
        }

        return orderItem
      })
    })
  },
)

const getFormattedInsuranceItems = createSelector(
  (state: App.State) => getInsuranceItemsView(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => getInsuranceItemDiscounts(state),
  (
    itemView,
    travellers,
    insuranceItemDiscounts,
  ) => {
    const primaryTraveller = travellers.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

    if (!primaryTraveller) {
      return []
    }

    if (!insuranceItemDiscounts.hasRequiredPromoCodeData) {
      return []
    }

    // views must have a quote associated with them -
    // those without a quote id are most likely post purchase updates and are handled via
    // the specific post purchase operation
    return itemView.data.filter(viewItem => viewItem.quoteId).map(viewItem => {
      const total = viewItem.totals.price

      return {
        type: ITEM_TYPE_INSURANCE,
        product_id: viewItem.item.productId,
        quote_id: viewItem.quoteId,
        brand: config.BRAND,
        transaction_key: viewItem.item.transactionKey,
        policy_holder_firstname: primaryTraveller.data.firstName,
        policy_holder_surname: primaryTraveller.data.lastName,
        travellers_details: travellers.map(
          traveller => ({
            firstname: traveller.data.firstName,
            surname: traveller.data.lastName,
            age_range: traveller.data.dateOfBirth ? getCoverGeniusAgeCategoryByDateOfBirth(traveller.data.dateOfBirth) : traveller.id.split('-')[0],
          })),
        insurance_type: viewItem.item.insuranceType,
        total: floatify(total),
        mobile_only_price: !!viewItem.mobileAppPrice,
        item_discounts: insuranceItemDiscounts.itemDiscounts.map(formatItemDiscount),
        flight_journeys_keys: viewItem.item.flightJourneysKeys,
      }
    })
  },
)

const getFormattedBookingProtectionItems = createSelector(
  (state: App.State) => getBookingProtectionItems(state),
  (state: App.State) => getBookingProtectionDetails(state),
  (bookingProtectionItems, bookingProtectionDetails): Array<App.Checkout.BookingProtectionDetails> | [] => {
    if (!bookingProtectionDetails) return []

    return bookingProtectionItems.map(item => ({
      type: ITEM_TYPE_BOOKING_PROTECTION,
      transaction_key: item.transactionKey,
      ...bookingProtectionDetails,
    }))
  },
)

export const getFormattedGiftCardItems = createSelector(
  (state: App.State) => state.auth.account,
  (state: App.State) => getGiftCardItems(state),
  (account, items) => {
    return items.map(item => ({
      type: ITEM_TYPE_GIFT_CARD,
      brand: config.BRAND,
      transaction_key: item.transactionKey,
      gift_card_value: item.amount,
      customer_email: account.email,
      customer_name: account.fullName,
      personalised: {
        method: item.deliveryMethod,
        to: item.content.recipientName,
        from: item.content.fromName,
        message: item.content.message,
        email_of_recipient: item.content.recipientEmail,
        is_blank_message: !item.content.message,
      },
      product: item.product ? {
        name: item.product.name,
        items: item.product.items,
        imageId: item.product.image.id,
        imageUrl: item.product.image.url,
        type: item.product.type,
        url: item.product.url,
        location: item.product.location,
      } : undefined,
    }))
  },
)

const selectFormattedBusinessTravellerCreditItem = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getFlightBreakdownView(state),
  (
    accommodationOfferViews,
    flightBreakdownViewsWithStatus,
  ): App.BusinessTraveller.CreditOrderItem | undefined => {
    let total = 0
    for (const offerView of accommodationOfferViews.data) {
      offerView.itemViews.forEach((itemView: App.Checkout.AnyItemView) => {
        total += itemView.totals.businessTravellerCredits ?? 0
      })
    }

    for (const flightBreakdownView of flightBreakdownViewsWithStatus.data) {
      total += flightBreakdownView.businessTravellerCredits ?? 0
    }

    if (total === 0) {
      return undefined
    }

    return {
      type: 'business_credit',
      transaction_key: uuidV4(),
      total,
    }
  },
)

const selectBusinessTravellerServiceFeeCartItem = createSelector(
  (state: App.State) => state.checkout.cart.cartId,
  (state: App.State) => state.businessTraveller.serviceFees,
  (state: App.State) => isLebtServiceFeesEnabled(state),
  (
    cartId,
    serviceFees,
    lebtServiceFeesEnabled,
  ): App.BusinessTraveller.BusinessServiceFeeCartItem | undefined => {
    if (!lebtServiceFeesEnabled) {
      return undefined
    }

    const serviceFee = serviceFees[cartId]
    if (!serviceFee?.amount || !serviceFee.source || !serviceFee.type) {
      return undefined
    }

    return {
      type: 'service_fee',
      transaction_key: uuidV4(),
      serviceFeeType: 'fixed_amount', // fixed amount is needed for compatibility with the legacy payments service fee
      serviceFeeSource: serviceFee.source,
      serviceFeeChargeType: serviceFee.type,
      ...(serviceFee.destination ? { serviceFeeDestination: serviceFee.destination } : {}),
      ...(serviceFee.product ? { serviceFeeProduct: serviceFee.product } : {}),
      conciergeMode: serviceFee.conciergeMode ?? false,
      serviceFeeAmount: serviceFee.amount,
    }
  },
)

export function formatCartItems(
  cartState: App.CheckoutCartState,
  connectedRoomOfferIds: Array<string> = [],
  offers: Record<string, App.AnyOffer>,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Record<string, App.AnyJourney>,
  account: App.AuthAccount,
  isTravellerFormNeeded: boolean,
  formSchemaRequest: { items: Array<any> },
  formattedInstantBookingHotelItems: Array<unknown>,
  formattedTransferItems: Array<unknown>,
  formattedInsuranceItems: Array<unknown>,
  getFormattedBookingProtectionItems: Array<unknown>,
  formattedBundleAndSaveItems: Array<unknown>,
  formattedGiftCardItems: Array<unknown>,
  formattedVillaItems: Array<unknown>,
  formattedBusinessTravellerCreditItem: App.BusinessTraveller.CreditOrderItem | undefined,
  formattedBusinessTravellerServiceFeeCartItems: App.BusinessTraveller.BusinessServiceFeeCartItem | undefined,
  existingOrderTraveller: App.ExistingOrderBedbankTraveller | undefined,
  promoCodeItemDiscounts: PromoItemDiscountsWithStatus,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
  channelMarkup?: App.ChannelMarkup,
): App.WithDataStatus<Array<any>> {
  const { itemDiscounts: allItemDiscounts, hasRequiredPromoCodeData } = promoCodeItemDiscounts

  if (
    isTravellerFormNeeded &&
    formSchemaRequest.items.length > 0 &&
    !travellerForms.some(form => form.id === PRIMARY_TRAVELLER_FORM_ID) ||
    !promoCodeItemDiscounts.hasRequiredPromoCodeData
  ) {
    return { hasRequiredData: false, data: [] }
  }

  let hasRequiredData = true

  const { items, specialRequests, arrivalFlightNumber } = cartState

  const formattedBNBLHotelItems = items
    .filter(isBNBLLEHotelItem)
    .map(item => {
      const offer = offers[item.offerId] as App.Offer
      if (!offer) {
        hasRequiredData = false
        return null
      }

      return formatLeHotelItem(
        item,
        travellerForms,
        specialRequests[item.itemId],
        offer,
        undefined,
        account,
        connectedRoomOfferIds,
        undefined,
        undefined,
        allItemDiscounts,
        channelMarkup,
      )
    })

  const formattedTourV1Items = items
    .filter(isTourV1Item)
    .map((item, idx) => {
      const offer = offers[item.offerId] as App.Offer
      const { itemDiscounts: allItemDiscounts } = promoCodeItemDiscounts

      if (!offer) {
        hasRequiredData = false
        return null
      }

      if (travellerForms[idx]) {
        const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'cart-id-based', cartItemIds: [item.itemId] } })
        return formatLeTourV1Item(item, travellerForms[idx], specialRequests[item.itemId], offer, account, itemDiscounts)
      }
    })

  const formattedCarHireItems = items
    .filter(isCarHireItem)
    .map((item) => formatCarHireItem(item, travellerForms, arrivalFlightNumber, allItemDiscounts))

  // Bedbank cart room items need to be grouped together
  // Until the implementation changes on `svc-order`
  // As of now, there will only ever be 1 group
  const groupedBedbankItems = [...groupBy(items.filter(isBedbankItem), item => item.sessionId).values()]

  const formattedBedbankItems = groupedBedbankItems.map(group => {
    const firstItem = group[0]
    const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)
    const offer = offers[firstItem.offerId] as App.BedbankOffer
    if (!offer) {
      hasRequiredData = false
      return null
    }
    const rooms = group.map(item => item.occupancy)
    const requests = group.map(item => item.itemId).map(id => specialRequests?.[id] ?? '').join(',')
    const itemDiscounts = filterItemDiscounts({
      itemDiscounts: allItemDiscounts,
      lookup: { specificType: 'accommodation' },
    })

    return formatBedbankItem(firstItem, requests, primaryTraveller, rooms, itemDiscounts, offer, existingOrderTraveller)
  }).filter(Boolean)

  const formattedFlightItems = items
    .filter(isFlightItem)
    .reduce<Array<unknown>>((formattedItems, item) => {
      let flightJourneys: Array<App.JourneyV2> = []

      if (item.searchId) {
        const keys = item.flights.map(flight => getJourneyV2IdKey(flight.journeyId, item.searchId!, flight.fareFamily?.id))
        flightJourneys = keys.map(key => journeys[key] as App.JourneyV2)
      }

      if (!flightJourneys[0] || !item.hasFinalFare) {
        hasRequiredData = false
      }

      const data = flightJourneys[0] ? [
        ...formattedItems,
        ...formatFlightItem(item, travellerForms, flightJourneys, allItemDiscounts, schemaData),
      ] : formattedItems

      return data
    }, [])

  // Check if data requirements satisfied
  hasRequiredData = hasRequiredData &&
    hasRequiredPromoCodeData

  const formattedItems = [
    ...formattedInstantBookingHotelItems,
    ...formattedBNBLHotelItems,
    ...formattedTourV1Items,
    ...formattedBedbankItems,
    ...formattedFlightItems,
    ...formattedTransferItems,
    ...formattedInsuranceItems,
    ...getFormattedBookingProtectionItems,
    ...formattedCarHireItems,
    ...formattedBundleAndSaveItems,
    ...formattedGiftCardItems,
    ...formattedVillaItems,
  ]

  if (formattedBusinessTravellerCreditItem) {
    formattedItems.push(formattedBusinessTravellerCreditItem)
  }

  if (formattedBusinessTravellerServiceFeeCartItems) {
    formattedItems.push({ ...formattedBusinessTravellerServiceFeeCartItems, transaction_key: uuidV4() })
  }

  return {
    hasRequiredData,
    data: formattedItems,
  }
}

function formatLeHotelItem(
  item: App.Checkout.InstantBookingLEHotelItem | App.Checkout.BNBLLEHotelItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  specialRequest: string,
  offer: App.Offer,
  instantBookingPricing: (App.Checkout.ItemViewTotals & {
    extraGuestSurchargesPayableAtProperty?: boolean,
  }) | undefined,
  account: App.AuthAccount,
  connectedRoomOfferIds: Array<string>,
  cartMode?: App.CheckoutCartMode,
  arrivalDetails?: App.ArrivalDetails,
  allItemDiscounts?: Array<App.Checkout.ItemDiscount>,
  channelMarkup?: App.ChannelMarkup,
) {
  let pkgUniqueKey = getPackageUniqueKey(item.packageId, item.duration, item.roomRateId)
  if (item.hasLoyaltyBenefitRoomUpgrade) {
    const originPackage = offer.packages.find(pkg => pkg.uniqueKey === pkgUniqueKey)
    if (originPackage?.loyalty.targetUpgradePackageUniqueKey) {
      const upgradePackage = offer.packages.find(pkg => pkg.uniqueKey === originPackage.loyalty.targetUpgradePackageUniqueKey)
      if (upgradePackage) {
        // @ts-expect-error string satisfies the magic string here
        pkgUniqueKey = upgradePackage.uniqueKey
      }
    }
  }
  const pkg = offer.packages.find(pkg => pkg.uniqueKey === pkgUniqueKey)

  if (!pkg) {
    throw new Error(`Package with unique key ${pkgUniqueKey} not found in offer ${offer.id}`)
  }

  // primaryTraveller will be absent on package upgrading
  const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const priceFields = {
    price: instantBookingPricing?.price ?? pkg.price ?? 0,
    memberPrice: (instantBookingPricing?.memberPrice || pkg.memberPrice) ?? 0,
    taxesAndFees: instantBookingPricing?.taxesAndFees ?? pkg.taxesAndFees ?? 0,
    surcharge: instantBookingPricing?.surcharge ?? 0,
    extraGuestSurcharge: instantBookingPricing?.extraGuestSurcharge ?? 0,
    extraGuestSurchargesPayableAtProperty: instantBookingPricing?.extraGuestSurchargesPayableAtProperty ?? false,
  }
  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts ?? [], lookup: { specificType: 'accommodationAndExperiences', itemIds: [item.itemId] } })

  const newBookingFields = {
    offer_id: offer.id,
    offer_type: offer.type,
    offer_name: offer.name,
    offer_slug: offer.slug,
    offer_booking_type: 'reservation',
    package_id: pkg.id,
    room_rate_id: pkg.roomRate?.id,
    package_name: pkg.name,
    number_of_nights: pkg.duration,
    value: pkg.value,
    localizedPrices: pkg.trackingPrice,
    price: priceFields.price,
    public_price: offer.type === OFFER_TYPE_ALWAYS_ON ? priceFields.price : undefined,
    lux_plus_price: offer.type === OFFER_TYPE_ALWAYS_ON && priceFields.memberPrice > 0 ? priceFields.memberPrice : undefined,
    surcharge: priceFields.surcharge,
    extra_guest_surcharge: priceFields.extraGuestSurchargesPayableAtProperty ? 0 : priceFields.extraGuestSurcharge,
    extra_guest_surcharge_payable_at_property: priceFields.extraGuestSurchargesPayableAtProperty ? priceFields.extraGuestSurcharge : 0,
    reservation_type: item.reservationType,
  }

  if (item.newPrice) {
    // accept a new price due to price change
    newBookingFields.price = item.newPrice
  }
  if (item.newSurcharge) {
    newBookingFields.surcharge = item.newSurcharge
  }
  if (item.newExtraGuestSurcharge) {
    newBookingFields.extra_guest_surcharge = item.newExtraGuestSurcharge
  }

  const selectDateFields = {
    ...(cartMode == 'select-date' && 'orderItemId' in item && {
      type: 'reservation',
      itemId: item.orderItemId,
      peak_period_surcharge: priceFields.surcharge,
      extra_guest_surcharge: priceFields.extraGuestSurcharge,
    }),
  }

  const arrivalDetailFields = {
    ...(arrivalDetails && {
      arrival_date: moment.utc(arrivalDetails.arrivalDate).format(ISO_DATE_FORMAT),
      arrival_time: arrivalDetails.arrivalTime,
      arrival_flight_number: arrivalDetails.arrivalFlightNumber,
    }),
  }

  let specialRequestWithConnectedRoomsMessage = specialRequest
  if (connectedRoomOfferIds.includes(offer.id)) {
    if (specialRequestWithConnectedRoomsMessage) {
      specialRequestWithConnectedRoomsMessage += ' '
    } else {
      specialRequestWithConnectedRoomsMessage = ''
    }
    specialRequestWithConnectedRoomsMessage = specialRequestWithConnectedRoomsMessage + getConnectRoomsSpecialRequestMessage(getContactName(primaryTraveller, account))
  }

  const data: any = {
    // common fields
    cart_item_id: item.itemId,
    transaction_key: item.transactionKey,
    guest_special_requests: specialRequestWithConnectedRoomsMessage,
    customer_email: primaryTraveller?.data.email || account.email,
    customer_name: account.fullName,
    arrival_details: arrivalDetailFields,
    item_discounts: itemDiscounts.map(formatItemDiscount),
    ...(cartMode === 'select-date' ? selectDateFields : newBookingFields),
    // Used for business budget
    number_of_nights: pkg.duration,
    longitude: offer.property?.longitude,
    latitude: offer.property?.latitude,
  }

  if (item.reservationType === RESERVATION_TYPE_INSTANT_BOOKING) {
    data.check_in = moment.utc(item.checkIn).format(ISO_DATE_FORMAT)
    data.check_out = moment.utc(item.checkOut).format(ISO_DATE_FORMAT)
    data.number_of_adults = item.occupancy.adults
    data.number_of_children = item.occupancy.children
    data.number_of_infants = item.occupancy.infants
    data.children_ages = item.occupancy.childrenAge ?? []
    data.rate_plan_id = pkg.roomRate?.id
    data.is_upgraded_rate = item.isUpgradedRate

    if (primaryTraveller) {
      data.guest_first_name = primaryTraveller.data.firstName
      data.guest_last_name = primaryTraveller.data.lastName
      data.guest_phone = (primaryTraveller.data.phonePrefix && primaryTraveller.data.phone) ? `${primaryTraveller.data.phonePrefix}${primaryTraveller.data.phone}` : undefined
    }

    if (OFFER_DYNAMIC_PRICING_TYPES.includes(offer.type)) {
      const instantBookingPrice = instantBookingPricing?.price ?? 0
      data.room_rate_amount = item.newPrice || instantBookingPrice
    }
  }

  if (channelMarkup && !channelMarkup.channelMarkupBlacklist.includes(offer.id)) {
    data.channel_markup_id = channelMarkup.channelMarkupId
  }

  return data
}

function formatLeTourV1Item(
  item: App.Checkout.LETourV1Item,
  form: App.Checkout.TravellerForm,
  specialRequest: string,
  offer: App.Offer,
  account: App.AuthAccount,
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
) {
  const pkg = offer.packages.find(pkg => pkg.id === item.packageId)
  if (!pkg) {
    throw new Error(`Package with id ${item.packageId} not found in offer ${offer.id}`)
  }
  const dob = form.data.dob || form.data.dateOfBirth
  const phone = (form.data.phonePrefix && form.data.phone) ? `${form.data.phonePrefix}${form.data.phone}` : undefined

  return {
    cart_item_id: item.itemId,
    transaction_key: item.transactionKey,

    offer_id: offer.id,
    offer_type: offer.type,
    offer_name: offer.name,
    offer_slug: offer.slug,

    package_id: pkg.id,
    room_rate_id: pkg.roomRate?.id,
    package_name: pkg.name,
    value: pkg.value,
    price: pkg.price,
    localizedPrices: pkg.trackingPrice,
    number_of_days: pkg.duration,

    offer_booking_type: 'reservation',
    reservation_type: RESERVATION_TYPE_INSTANT_BOOKING,
    guest_special_requests: specialRequest,

    customer_email: account.email,
    customer_name: account.fullName,

    start_date: moment.utc(item.startDate).format(ISO_DATE_FORMAT),
    end_date: moment.utc(item.endDate).format(ISO_DATE_FORMAT),

    guest_first_name: form.data.firstName,
    guest_middle_name: form.data.middleName,
    guest_last_name: form.data.lastName,
    guest_title: form.data.title,
    guest_phone: phone,

    dob: moment.utc(dob).format(ISO_DATE_FORMAT),
    postcode: form.data.postcode,
    item_discounts: itemDiscounts.map(formatItemDiscount) ?? [],
  }
}

function formatCarHireItem(
  item: App.Checkout.CarHireItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  arrivalFlightNumber: string | null,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
) {
  const primaryDriver = travellerForms.find((form) => form.id === PRIMARY_TRAVELLER_FORM_ID)
  const offer = item.offer

  const selectedRateOption = getCarHireSelectedRateOption(item)

  const totalPrice = selectedRateOption.payNowAmount + (item.selectedInsurance?.total || 0)
  const payOnPickUpAmount = selectedRateOption.payOnArrivalAmount + sum(item.selectedAddons, addon => addon.total ?? 0)

  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'car_hire' } })

  return {
    transaction_key: item.transactionKey,
    type: 'car_hire',
    offer,
    pickUpLocation: {
      code: offer.pickUp.id,
      date: moment.utc(`${item.pickUpDate}T${item.pickUpTime}`),
    },
    returnLocation: {
      code: offer.dropOff.id,
      date: moment.utc(`${item.dropOffDate}T${item.dropOffTime}`),
    },
    reference: {
      dateTime: selectedRateOption.dateTime,
      id: selectedRateOption.id,
      idContext: selectedRateOption.idContext,
      type: selectedRateOption.type,
      url: selectedRateOption.url,
    },
    vehicle: {
      code: offer.vehicle.code,
      model: offer.vehicle.model,
      image: offer.vehicle.image.url,
      transmissionType: offer.vehicle.transmission,
      baggageQuantity: offer.vehicle.luggage,
      passengerQuantity: offer.vehicle.seats,
      fuelType: offer.vehicle.fuelType,
      doorCount: offer.vehicle.doors,
      category: offer.vehicle.type,
      driveType: offer.vehicle.driveType,
      size: {
        code: offer.vehicle.sizeCode,
        description: offer.vehicle.type,
      },
      type: {
        code: offer.vehicle.typeCode,
        description: offer.vehicle.type,
      },
    },
    driver: {
      givenName: primaryDriver?.data.firstName,
      surname: primaryDriver?.data.lastName,
      email: primaryDriver?.data.email,
      phoneNumber: primaryDriver?.data.phone,
      phoneCode: primaryDriver?.data.phonePrefix,
      age: primaryDriver?.data.dateOfBirth ? getAge(primaryDriver.data.dateOfBirth, new Date(item.pickUpDate)) : undefined,
    },
    value: item.value,
    total: totalPrice,
    payOnPickUpAmount,
    originalPrice: selectedRateOption.originalPrice,
    qtyPassengers: 1, // TODO car hire remove mock
    availabilityUuid: offer.availabilityUuid,
    ...(arrivalFlightNumber && {
      arrivalDetails: {
        number: arrivalFlightNumber,
        transportationCode: TRANSPORTATION_CODE.AIRPLANE,
      },
    }),
    ...(item.selectedAddons && {
      specialEquipments: Object.values(item.selectedAddons),
    }),
    ...(item.selectedInsurance && item.selectedInsurance.type === 'enhanced' && {
      insuranceReference: {
        id: item.selectedInsurance.id,
        amount: item.selectedInsurance.total,
        currencyCode: item.selectedInsurance.currency,
        url: item.selectedInsurance.url,
      },
    }),
    item_discounts: itemDiscounts.map(formatItemDiscount),
    ...(item.bundledOrderId && {
      bundledOrderId: item.bundledOrderId,
    }),
  }
}

function formatBedbankItem(
  item: App.Checkout.BedbankHotelItem,
  specialRequests: string,
  primaryForm: App.Checkout.TravellerForm | undefined,
  rooms: Array<App.Occupants>,
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
  offer: App.BedbankOffer,
  existingOrderTraveller?: App.ExistingOrderBedbankTraveller,
) {
  let travellerFormData = {
    bookingPhone: primaryForm?.data.phonePrefix && primaryForm?.data.phone && `+${primaryForm.data.phonePrefix}${primaryForm.data.phone}`,
    bookingEmail: primaryForm?.data.email,
    guestFirstName: primaryForm?.data.firstName,
    guestLastName: primaryForm?.data.lastName,
    hotelLoyaltyProgramMemberId: primaryForm?.data.hotelLoyaltyProgramMemberId,
  }

  if (existingOrderTraveller) {
    travellerFormData = {
      ...travellerFormData,
      ...existingOrderTraveller,
    }
  }
  const loyatyProgramId = offer.property.chainName ?? offer.property.name

  return {
    brand: config.BRAND,
    type: ITEM_TYPE_BEDBANK,
    session_id: item.sessionId,
    bed_group_id: item.bedGroupId,
    property_id: item.offerId,
    room_type_id: item.roomId,
    room_rate_id: item.roomRateId,
    is_flight_bundle: item.isFlightBundle,
    transaction_key: item.transactionKey,
    booking_email: travellerFormData?.bookingEmail,
    booking_phone: travellerFormData.bookingPhone,
    rooms: rooms.map(roomOccupants => ({
      guest_first_name: travellerFormData.guestFirstName,
      guest_last_name: travellerFormData.guestLastName,
      guest_special_requests: specialRequests,
      hotel_loyalty_program: loyatyProgramId && travellerFormData.hotelLoyaltyProgramMemberId ? {
        program_id: loyatyProgramId,
        member_id: travellerFormData.hotelLoyaltyProgramMemberId,
      } : undefined,
      occupancy: generateOccupancyStringByRoom(roomOccupants),
    })),
    item_discounts: itemDiscounts.map(formatItemDiscount),
    is_upgraded_rate: item.isUpgradedRate,
    // added to calculate the business budget
    longitude: offer.property.longitude,
    latitude: offer.property.latitude,
    number_of_nights: item.duration,
  }
}

function formatPassengers(
  travellerForms: Array<App.Checkout.TravellerForm>,
  flightItem: App.Checkout.FlightItem,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
) {
  if (travellerForms.length === 0 || !flightItem?.flights[0]) { return [] }
  const carrierCodes = flightItem.flights.map((f) => f.carrier)
  const paxList = flightItem.passengers

  const paxBaggageMap = flightItem.flights.map(flight => flight.extras.baggage)
  const primaryForm = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const result = paxList.map((pax: App.Checkout.FlightPassenger) => {
    const paxFormData = travellerForms.find(form => pax.id === form.id)
    if (!paxFormData) { return null }
    const paxSchema = schemaData?.accommodationFlightSchema.properties[pax.id]

    const allBaggage = paxBaggageMap.map(baggage => baggage[pax.id])
    const baggage = buildBaggagePayload(allBaggage[0], allBaggage[1])
    return buildPassengerPayload(pax, paxFormData, primaryForm, baggage, carrierCodes, paxSchema)
  }).filter(excludeNullOrUndefined)

  return result
}

function formatFlightItem(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
): Array<unknown> {
  const isReturn = journeys.length === 2
  const isSameProvider = isReturn && new Set(journeys.map(journey => journey.provider)).size === 1

  if (item.viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN || item.viewType === FlightViewTypes.MULTI_CITY || isSameProvider) {
    return formatReturnFlightItemV2(item, travellerForms, journeys, allItemDiscounts, schemaData)
  }

  return formatFlightItemV2(item, travellerForms, journeys, allItemDiscounts, schemaData)
}

function formatFlightItemV2(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
) {
  if (travellerForms.length === 0) { return [] }
  const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const result = item.flights.map((flight, index) => {
    const segmentJourney = journeys[index]

    if (segmentJourney) {
      const baggageOptions = baggageOptionsMap(segmentJourney.flightGroup.flights[0])?.baggage ?? []

      const cost = sum([
        segmentJourney.price.all.totalFare,
        sum(Object.values(item?.otherFees ?? [])),
        computeBaggageCostForPaxList(item.passengers, baggageOptions, flight.extras.baggage),
      ])

      const tax = segmentJourney.price.all.totalTax

      const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'flight' } })

      return {
        provider: segmentJourney.provider,
        transaction_key: item.transactionKey,
        type: CHECKOUT_ITEM_TYPE_FLIGHT,
        offer_type: [(item.bundledItemIds?.length ?? 0) > 0 ? 'hotel' : null],
        contact_email_address: primaryTraveller?.data.email,
        contact_phone_number: primaryTraveller ? `${primaryTraveller?.data.phonePrefix}${primaryTraveller?.data.phone}` : undefined,
        passengers: formatPassengers(travellerForms, item, schemaData),
        journey: {
          id: flight.journeyId,
          cost,
          tax,
          searchId: item.searchId,
        },
        viewType: item.viewType,
        item_discounts: itemDiscounts.map(formatItemDiscount),
        // Used for the business budget
        departure_airport_code: item.originAirportCode,
        arrival_airport_code: item.destinationAirportCode,
      }
    }
  }).filter(excludeNullOrUndefined)

  return result
}

function formatReturnFlightItemV2(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
) {
  if (travellerForms.length === 0) { return [] }
  const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const cost = sum([
    ...item.flights.map(flight => flight.cost),
    sum(Object.values(item?.otherFees ?? [])),
    ...journeys.map((journey, index) => {
      const baggageOptions = baggageOptionsMap(journey.flightGroup.flights[0])?.baggage ?? []
      return computeBaggageCostForPaxList(item.passengers, baggageOptions, item.flights[index]?.extras.baggage ?? {})
    }),
  ])

  const tax = sum(journeys.map(journey => journey.price.all.totalTax))

  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'flight' } })

  const result = {
    provider: journeys[0].provider,
    transaction_key: item.transactionKey,
    type: CHECKOUT_ITEM_TYPE_FLIGHT,
    offer_type: [(item.bundledItemIds?.length ?? 0) ? 'hotel' : null],
    contact_email_address: primaryTraveller?.data.email,
    contact_phone_number: primaryTraveller ? `${primaryTraveller.data.phonePrefix}${primaryTraveller.data.phone}` : undefined,
    passengers: formatPassengers(travellerForms, item, schemaData),
    journey: {
      id: journeys[0].id,
      cost,
      tax,
      searchId: item.searchId,
      selectedFareIds: item.flights.map(flight => flight.journeyId),
    },
    viewType: item.viewType === FlightViewTypes.MULTI_CITY ?
      FlightViewTypes.MULTI_CITY :
      FlightViewTypes.TWO_ONE_WAYS_AND_RETURN,
    item_discounts: itemDiscounts.map(formatItemDiscount),
    // Used for the business budget
    departure_airport_code: item.originAirportCode,
    arrival_airport_code: item.destinationAirportCode,
  }

  return [result]
}

const getFormattedLeInstantBookingHotelItems = createSelector(
  (state: App.State) => getLeInstantBookingItems(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.calendar.calendarsByOccupancy,
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => state.auth.account,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getAccommodationAndExperienceItemDiscounts(state),
  (state: App.State) => getChannelMarkup(state),
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (
    items,
    offers,
    calendarsRatesByOccupancy,
    availableRatesByOccupancy,
    travellerForms,
    specialRequests,
    account,
    cartMode,
    promoCodeItemDiscounts,
    channelMarkup,
    connectedRoomOfferIds = [],
  ) => {
    return items
      .filter(isInstantBookingLEHotelItem)
      .map((item) => {
        const offer = offers[item.offerId]
        if (!offer) {
          return null
        }

        const {
          data: instantBookingPricing,
          hasRequiredData,
        } = getInstantBookingPriceAndSurcharge(item, offer, calendarsRatesByOccupancy, availableRatesByOccupancy)

        const { itemDiscounts } = promoCodeItemDiscounts
        if (!hasRequiredData) {
          return null
        }
        const specificItemDiscounts = filterItemDiscounts({ itemDiscounts, lookup: { specificType: 'accommodation' } })

        return formatLeHotelItem(
          item,
          travellerForms,
          specialRequests[item.itemId],
          offer,
          instantBookingPricing,
          account,
          connectedRoomOfferIds,
          cartMode,
          undefined,
          specificItemDiscounts,
          channelMarkup
        )
      })
  },
)

export const getFormattedVillaItems = createSelector(
  (state: App.State) => getVillaViews(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => state.auth.account,
  (state: App.State) => state.checkout.cart.arrivalDetails,
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (state: App.State) => getChannelMarkup(state),
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (
    views,
    travellerForms,
    specialRequests,
    account,
    arrivalDetails,
    promoCodeItemDiscounts,
    channelMarkup,
    connectedRoomOfferIds = [],
  ) => {
    return views.data.map(view => {
      const { itemDiscounts: allItemDiscounts } = promoCodeItemDiscounts
      const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'accommodation', cartItemIds: [view.item.itemId] } })
      return formatLeHotelItem(
        view.item,
        travellerForms,
        specialRequests[view.item.itemId],
        view.offer,
        view.totals,
        account,
        connectedRoomOfferIds,
        undefined,
        arrivalDetails[view.item.itemId],
        itemDiscounts,
        channelMarkup,
      )
    })
  },
)

export const getFormattedCartItems = createSelector(
  (state: App.State) => state.checkout.cart,
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => state.auth.account,
  (state: App.State) => isTravellerFormNeeded(state),
  (state: App.State) => getTravellerFormSchemaRequest(state),
  (state: App.State) => getFormattedLeInstantBookingHotelItems(state),
  (state: App.State) => getFormattedTransferItems(state),
  (state: App.State) => getFormattedInsuranceItems(state),
  (state: App.State) => getFormattedBookingProtectionItems(state),
  (state: App.State) => getFormattedBundleAndSaveItems(state),
  (state: App.State) => getFormattedGiftCardItems(state),
  (state: App.State) => getFormattedVillaItems(state),
  (state: App.State) => selectFormattedBusinessTravellerCreditItem(state),
  (state: App.State) => selectBusinessTravellerServiceFeeCartItem(state),
  (state: App.State) => getBedbankChangeDatesTravellers(state),
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getChannelMarkup(state),
  formatCartItems,
)

export const getOrderGift = createSelector(
  (state: App.State) => state.checkout.form.travellerForms,
  (travellers): Order.OrderGift | undefined => {
    if (travellers.length < 2) { return undefined }
    const giftRecipient = travellers.find(traveller => traveller.id === GIFT_RECIPIENT_FORM_ID)
    if (giftRecipient) {
      const giftGiver = travellers.find(traveller => traveller.id === PRIMARY_TRAVELLER_FORM_ID)
      const giftPayload: Order.OrderGift = {
        receiver_email: giftRecipient.data.email!,
        receiver_name: giftRecipient ? `${giftRecipient.data.firstName} ${giftRecipient.data.lastName}` : 'undefined',
        giver_name: giftGiver ? `${giftGiver.data.firstName} ${giftGiver.data.lastName}` : '',
        scheduled_date: generateScheduleDate(giftRecipient.data.scheduledDate!),
      }
      if (giftRecipient.data.personalMessage) {
        giftPayload.gift_message = giftRecipient.data.personalMessage
      }
      return giftPayload
    }
  },
)
